import {
  Badge,
  DataTable,
  DescriptionList,
  Modal,
  SelectableList,
  type BadgeColor,
  type DataTableColumn,
  type DataTableFooterRow,
  type DataTableRow,
  type DataTableSortDirection,
  type DescriptionListItem,
  type SelectableListItem,
} from '@enamelai/design-system'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import { BALANCE_BADGE_COLOR } from '@constants/Billing'
import {
  BalanceBadgeStatus,
  BillingProcedure,
  LedgerEntry,
} from '@dataTypes/billing.types'
import { formatCurrency } from '@utils/billing.utils'
import { mapClaimStatus } from '@utils/claims.util'

const CLAIM_STATUS_LABELS: Record<string, string> = {
  approved: StatusTypes.Approved,
  rejected: StatusTypes.Rejected,
  'under review': StatusTypes.UnderReview,
  'partially approved': StatusTypes.PartiallyApproved,
  cancelled: StatusTypes.Cancelled,
  denied: StatusTypes.Denied,
}

const BILLING_STATUS_LABELS: Record<string, string> = {
  paid: StatusTypes.Paid,
  'partially paid': StatusTypes.PartiallyPaid,
  unpaid: StatusTypes.Unpaid,
  unbilled: StatusTypes.Unbilled,
}

const PROCEDURE_COLUMNS: DataTableColumn[] = [
  { id: 'procedureCode', label: 'Code', sortable: true, width: '110px' },
  { id: 'clinicFee', label: 'Clinic fee', sortable: true, width: '110px' },
  {
    id: 'allowedAmount',
    label: 'Allowed amt.',
    sortable: true,
    width: '110px',
  },
  { id: 'writeOff', label: 'Write-off', sortable: true, width: '110px' },
  {
    id: 'estimatedInsurance',
    label: 'Estimate ins.',
    sortable: true,
    width: '110px',
  },
  {
    id: 'approvedInsurance',
    label: 'Approved ins.',
    sortable: true,
    width: '110px',
  },
  {
    id: 'insuranceAdjustment',
    label: 'Insurance adj.',
    sortable: true,
    width: '110px',
  },
  { id: 'discount', label: 'Discount', sortable: true, width: '110px' },
  {
    id: 'patientResponsibility',
    label: 'Patient resp.',
    sortable: true,
    width: '110px',
  },
  {
    id: 'patientAdjustment',
    label: 'Patient adj.',
    sortable: true,
    width: '110px',
  },
]

const appointmentStatusLabel = (appointmentStatus: AppointmentStatus): string =>
  StatusTypes[
    AppointmentStatus[appointmentStatus] as keyof typeof StatusTypes
  ] ?? ''

const statusBadgeColor = (status: string): BadgeColor => {
  switch (status) {
    case StatusTypes.Confirmed:
    case StatusTypes.CheckIn:
    case StatusTypes.Approved:
    case StatusTypes.Paid:
      return 'vivid-green'
    case StatusTypes.Completed:
    case StatusTypes.FollowUp:
    case StatusTypes.InTreatment:
    case StatusTypes.Draft:
      return 'blue'
    case StatusTypes.NewPatient:
    case StatusTypes.CheckOut:
      return 'teal'
    case StatusTypes.Alert:
    case StatusTypes.Cancelled:
    case StatusTypes.Late:
    case StatusTypes.Rejected:
    case StatusTypes.Denied:
    case StatusTypes.Unpaid:
    case StatusTypes.Unbilled:
      return 'red'
    case StatusTypes.NoShow:
    case StatusTypes.Unconfirmed:
    case StatusTypes.UnderReview:
    case StatusTypes.PartiallyPaid:
      return 'yellow'
    case StatusTypes.PartiallyApproved:
      return 'pale-green'
    default:
      return 'gray'
  }
}

const statusBadge = (status: string): React.ReactElement => (
  <Badge mode="tinted" color={statusBadgeColor(status)} label={status} />
)

const displayText = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? 'N/A' : String(value)

const displayDate = (value: string | null | undefined): string => {
  if (typeof value === 'string' && value) {
    return new Date(value).toLocaleDateString()
  }
  return 'N/A'
}

const displayOptionalCurrency = (value: number | null | undefined): string =>
  typeof value === 'number' ? formatCurrency(value) : 'N/A'

const mappedStatusLabel = (
  value: string | null | undefined,
  labels: Record<string, string>,
): string | null => {
  if (!value) return null
  return labels[value.trim().toLowerCase()] ?? value
}

const claimStatusLabel = (value: string | null | undefined): string | null => {
  if (!value) return null
  const mapped = CLAIM_STATUS_LABELS[mapClaimStatus(value).status.toLowerCase()]
  if (mapped) return mapped
  return mappedStatusLabel(value, CLAIM_STATUS_LABELS)
}

const statusValue = (status: string | null): React.ReactNode =>
  status ? statusBadge(status) : 'N/A'

const currencyWithBalanceBadge = (
  amount: number,
  balance: number,
  status: BalanceBadgeStatus,
): React.ReactElement => (
  <span className="d-flex align-items-center gap-2">
    <span>{formatCurrency(amount)}</span>
    <Badge
      mode="tinted"
      color={BALANCE_BADGE_COLOR[status]}
      label={formatCurrency(balance)}
    />
  </span>
)

const insuranceBalanceStatus = (
  entry: LedgerEntry,
  procedures: BillingProcedure[],
): BalanceBadgeStatus => {
  const estimatedInsurance = entry.estimatedInsurance || 0
  const total = entry.approvedInsurance || 0
  const insuranceCashAndAdjustment = procedures.reduce(
    (sum, procedure) =>
      sum +
      (procedure.insuranceCash || 0) +
      (procedure.insuranceAdjustment || 0),
    0,
  )
  if (
    estimatedInsurance <= 0 ||
    total <= 0 ||
    insuranceCashAndAdjustment <= 0
  ) {
    return BalanceBadgeStatus.Unpaid
  }
  if (
    (entry.insuranceBalance || 0) === 0 &&
    insuranceCashAndAdjustment >= total
  ) {
    return BalanceBadgeStatus.Paid
  }
  return BalanceBadgeStatus.Partial
}

const patientBalanceStatus = (
  entry: LedgerEntry,
  procedures: BillingProcedure[],
): BalanceBadgeStatus => {
  const balance = entry.patientBalance || 0
  const patientCashAndAdjustment = procedures.reduce(
    (sum, procedure) =>
      sum + (procedure.patientCash || 0) + (procedure.patientAdjustment || 0),
    0,
  )
  if (patientCashAndAdjustment > 0 && balance !== 0) {
    return BalanceBadgeStatus.Partial
  }
  if (balance === 0 && patientCashAndAdjustment > 0) {
    return BalanceBadgeStatus.Paid
  }
  return BalanceBadgeStatus.Unpaid
}

const PROCEDURE_SORT_FIELDS: Record<string, keyof BillingProcedure> = {
  procedureCode: 'procedureCode',
  clinicFee: 'clinicFee',
  allowedAmount: 'allowedAmount',
  writeOff: 'writeOff',
  estimatedInsurance: 'estimatedInsurance',
  approvedInsurance: 'approvedInsurance',
  insuranceAdjustment: 'insuranceAdjustment',
  discount: 'discount',
  patientResponsibility: 'patientResponsibility',
  patientAdjustment: 'patientAdjustment',
}

const compareProcedureValues = (
  left: string | number | null | undefined,
  right: string | number | null | undefined,
  direction: DataTableSortDirection,
): number => {
  if (left == null && right == null) return 0
  if (left == null) return 1
  if (right == null) return -1
  const multiplier = direction === 'asc' ? 1 : -1
  if (typeof left === 'number' && typeof right === 'number') {
    return (left - right) * multiplier
  }
  return String(left).localeCompare(String(right)) * multiplier
}

interface Props {
  isVisible: boolean
  closeModal: () => void
  ledgerEntries: LedgerEntry[]
  initialAppointmentId?: string
}

const ViewAppointmentInfo: React.FC<Props> = ({
  isVisible,
  closeModal,
  ledgerEntries,
  initialAppointmentId,
}) => {
  const [selectedLedgerEntry, setSelectedLedgerEntry] =
    useState<LedgerEntry | null>(null)
  const [sortColumnId, setSortColumnId] = useState<string | undefined>()
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  useEffect(() => {
    if (isVisible && ledgerEntries.length > 0) {
      const initialEntry = initialAppointmentId
        ? ledgerEntries.find(
            (entry) => entry.appointmentId === initialAppointmentId,
          )
        : ledgerEntries[0]
      setSelectedLedgerEntry(initialEntry || ledgerEntries[0])
    }
  }, [isVisible, ledgerEntries, initialAppointmentId])

  const selectedProcedures = useMemo(
    () => selectedLedgerEntry?.procedures ?? [],
    [selectedLedgerEntry],
  )

  const procedureRows = useMemo((): DataTableRow[] => {
    const sortField = sortColumnId
      ? PROCEDURE_SORT_FIELDS[sortColumnId]
      : undefined
    const procedures = sortField
      ? [...selectedProcedures].sort((left, right) =>
          compareProcedureValues(
            left[sortField],
            right[sortField],
            sortDirection,
          ),
        )
      : selectedProcedures
    return procedures.map((procedure, index) => ({
      id: procedure.procedureLogId || `procedure-${index}`,
      procedureCode: procedure.procedureCode,
      clinicFee: formatCurrency(procedure.clinicFee),
      allowedAmount: formatCurrency(procedure.allowedAmount),
      writeOff: formatCurrency(procedure.writeOff),
      estimatedInsurance: formatCurrency(procedure.estimatedInsurance),
      approvedInsurance: formatCurrency(procedure.approvedInsurance),
      insuranceAdjustment: formatCurrency(procedure.insuranceAdjustment),
      discount: formatCurrency(procedure.discount),
      patientResponsibility: formatCurrency(procedure.patientResponsibility),
      patientAdjustment: formatCurrency(procedure.patientAdjustment),
    }))
  }, [selectedProcedures, sortColumnId, sortDirection])

  const handleProcedureSort = useCallback(
    (columnId: string): void => {
      if (sortColumnId === columnId) {
        setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'))
        return
      }
      setSortColumnId(columnId)
      setSortDirection('asc')
    },
    [sortColumnId],
  )

  const historyItems = useMemo(
    (): SelectableListItem[] =>
      ledgerEntries.map((entry) => {
        const status = appointmentStatusLabel(entry.appointmentStatus)
        return {
          id: entry.appointmentId,
          primary: entry.appointmentType,
          secondary: new Date(entry.appointmentDate).toLocaleDateString(),
          meta: formatCurrency(entry.clinicFee),
          status: statusBadge(status),
        }
      }),
    [ledgerEntries],
  )

  const procedureTotals = useMemo(
    () =>
      selectedProcedures.reduce(
        (acc, procedure) => ({
          clinicFee: acc.clinicFee + (procedure.clinicFee || 0),
          allowedAmount: acc.allowedAmount + (procedure.allowedAmount || 0),
          writeOff: acc.writeOff + (procedure.writeOff || 0),
          estimatedInsurance:
            acc.estimatedInsurance + (procedure.estimatedInsurance || 0),
          approvedInsurance:
            acc.approvedInsurance + (procedure.approvedInsurance || 0),
          insuranceAdjustment:
            acc.insuranceAdjustment + (procedure.insuranceAdjustment || 0),
          discount: acc.discount + (procedure.discount || 0),
          patientResponsibility:
            acc.patientResponsibility + (procedure.patientResponsibility || 0),
          patientAdjustment:
            acc.patientAdjustment + (procedure.patientAdjustment || 0),
        }),
        {
          clinicFee: 0,
          allowedAmount: 0,
          writeOff: 0,
          estimatedInsurance: 0,
          approvedInsurance: 0,
          insuranceAdjustment: 0,
          discount: 0,
          patientResponsibility: 0,
          patientAdjustment: 0,
        },
      ),
    [selectedProcedures],
  )

  const postingGroups = useMemo((): DescriptionListItem[][] => {
    if (!selectedLedgerEntry) return []
    const appointmentStatus = appointmentStatusLabel(
      selectedLedgerEntry.appointmentStatus,
    )
    return [
      [
        { label: 'Patient name', value: selectedLedgerEntry.patientName },
        {
          label: 'Appointment type',
          value: selectedLedgerEntry.appointmentType,
        },
        {
          label: 'Appointment date',
          value: displayDate(selectedLedgerEntry.appointmentDate),
        },
        {
          label: 'Appointment status',
          value: statusValue(appointmentStatus || null),
        },
      ],
      [
        {
          label: 'Provider',
          value: displayText(selectedLedgerEntry.providerName),
        },
        { label: 'Claim ID', value: displayText(selectedLedgerEntry.claimId) },
        {
          label: 'Claim submitted',
          value: displayText(selectedLedgerEntry.claimSubmitted),
        },
        {
          label: 'Claim status',
          value: statusValue(claimStatusLabel(selectedLedgerEntry.claimStatus)),
        },
      ],
      [
        { label: 'Paid by', value: displayText(selectedLedgerEntry.paidBy) },
        {
          label: 'Payment method',
          value: displayText(selectedLedgerEntry.paymentMethod),
        },
        {
          label: 'Payment date',
          value: displayDate(selectedLedgerEntry.paymentDate),
        },
        {
          label: 'Billing status',
          value: statusValue(
            mappedStatusLabel(
              selectedLedgerEntry.status,
              BILLING_STATUS_LABELS,
            ),
          ),
        },
      ],
      [
        {
          label: 'Insurance adj. type',
          value: displayText(selectedLedgerEntry.insuranceAdjustmentType),
        },
        {
          label: 'Patient adj. type',
          value: displayText(selectedLedgerEntry.patientAdjustmentType),
        },
        {
          label: 'Appointment adjustment',
          value: displayOptionalCurrency(
            selectedLedgerEntry.appointmentAdjustment,
          ),
        },
        {
          label: 'Appointment balance',
          value: displayOptionalCurrency(
            selectedLedgerEntry.appointmentBalance,
          ),
        },
      ],
    ]
  }, [selectedLedgerEntry])

  const footerRow = useMemo((): DataTableFooterRow | undefined => {
    if (!selectedLedgerEntry) return undefined
    return {
      procedureCode: 'Total',
      clinicFee: formatCurrency(procedureTotals.clinicFee),
      allowedAmount: formatCurrency(procedureTotals.allowedAmount),
      writeOff: formatCurrency(procedureTotals.writeOff),
      estimatedInsurance: formatCurrency(procedureTotals.estimatedInsurance),
      approvedInsurance: currencyWithBalanceBadge(
        procedureTotals.approvedInsurance,
        selectedLedgerEntry.insuranceBalance || 0,
        insuranceBalanceStatus(selectedLedgerEntry, selectedProcedures),
      ),
      insuranceAdjustment: formatCurrency(procedureTotals.insuranceAdjustment),
      discount: formatCurrency(procedureTotals.discount),
      patientResponsibility: currencyWithBalanceBadge(
        procedureTotals.patientResponsibility,
        selectedLedgerEntry.patientBalance || 0,
        patientBalanceStatus(selectedLedgerEntry, selectedProcedures),
      ),
      patientAdjustment: formatCurrency(procedureTotals.patientAdjustment),
    }
  }, [procedureTotals, selectedLedgerEntry, selectedProcedures])

  const handleHistoryChange = (appointmentId: string): void => {
    const entry = ledgerEntries.find(
      (ledgerEntry) => ledgerEntry.appointmentId === appointmentId,
    )
    if (entry) setSelectedLedgerEntry(entry)
  }

  return (
    <Modal
      title="Appointment info"
      open={isVisible}
      onClose={closeModal}
      size="xl"
      closeOnBackdrop={false}
      primaryLabel=""
      secondaryLabel="Close"
      onSecondaryClick={closeModal}
      style={{ maxWidth: 'min(1800px, calc(100vw - 48px))' }}
    >
      <Row className="gx-2 flex-nowrap">
        <Col xs="auto" className="flex-shrink-0">
          <SelectableList
            title="Appointment history"
            titleMeta={`${ledgerEntries.length} ${
              ledgerEntries.length === 1 ? 'appt' : 'appts'
            }`}
            items={historyItems}
            value={selectedLedgerEntry?.appointmentId}
            onChange={handleHistoryChange}
            width={265}
            maxHeight={600}
          />
        </Col>
        <Col>
          {selectedLedgerEntry && (
            <div className="mb-2">
              <DescriptionList groups={postingGroups} variant="panel" />
            </div>
          )}
          <DataTable
            columns={PROCEDURE_COLUMNS}
            rows={procedureRows}
            footerRow={footerRow}
            showActions={false}
            sortColumnId={sortColumnId}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleProcedureSort}
          />
        </Col>
      </Row>
    </Modal>
  )
}

export default ViewAppointmentInfo
